import { getSelectOption } from '@components/inputs/utils'
import { UserLeadInfo } from '@grammarly/http-clients-analytics/lib/analytics'
import { O } from '@grammarly/util-fp-ts'
import { ApiService } from '@lib/services/api'
import { GeoLocationService } from '@lib/services/geo-location'
import { TypeComponentFormFields } from '@lib/types/component-form'
import { TypeComponentSelect } from '@lib/types/component-select'

function normalizeUserLeadInfo(userLeadInfo: UserLeadInfo, country?: string) {
  // Mapping to inputs (name field) in the Contentful form.
  return {
    firstName: O.toUndefined(userLeadInfo.firstName),
    lastName: O.toUndefined(userLeadInfo.lastName),
    organizationName: O.toUndefined(userLeadInfo.companyName), // account_name is stored under companyName
    companyName: O.toUndefined(userLeadInfo.companyName),
    companySize: O.toUndefined(userLeadInfo.companySize),
    managementLevel: O.toUndefined(userLeadInfo.seniorityType),
    function: O.toUndefined(userLeadInfo.teamType),
    country,
  }
}

async function fetchUserLeadInfo(analytics: ApiService['analytics'], email: string): Promise<UserLeadInfo | null> {
  try {
    return await analytics.getUserLeadInfoByEmail(email)
  } catch (_error) {
    return null
  }
}

// Comes from https://gitlab.grammarly.io/acquisition/analytics/-/blob/master/grammarly-analytics-common/src/main/java/com/grammarly/analytics/web/rest/dto/UserLeadDirectoryTeamType.java
// Values from function input in Contentful: https://app.contentful.com/spaces/1e6ajr2k4140/environments/master/entries/5kE48m6CoCfXJncdy3Wwzv
function remapTeamTypeValue(value: string) {
  switch (value) {
    case 'CREATIVE_DESIGN':
      return 'Communications'
    case 'CUSTOMER_SUCCESS_ACCOUNT_MANAGEMENT':
    case 'CUSTOMER_EXPERIENCE':
      return 'Customer Support'
    case 'DATA':
    case 'ENGINEERING':
      return 'Engineering'
    case 'HUMAN_RESOURCES':
      return 'HR and recruiting'
    case 'INFORMATION_TECHNOLOGY':
      return 'IT'
    case 'MARKETING':
      return 'Marketing'
    case 'SALES':
      return 'Sales'
    case 'OTHER':
    default:
      return !value ? '' : 'Other'
  }
}

// Values from company size input in Contentful: https://app.contentful.com/spaces/1e6ajr2k4140/environments/master/entries/3lGfcn8rLZ88NDYDXWaCeX
function getCompanySizeOption(selectInput: TypeComponentSelect, companySize: number): string {
  if (companySize < 50) {
    return 'SMB - Chat'
  } else if (companySize < 250) {
    return 'SMB - Sales Assist'
  } else if (companySize < 5000) {
    return 'Commercial'
  } else {
    return 'Enterprise'
  }
}

function findMatchingSelectOption(selectInput: TypeComponentSelect, value: string) {
  return (
    selectInput.fields.options.find((option) => {
      const optionValue = getSelectOption(option).value
      return optionValue.toLowerCase() === value.toLowerCase()
    }) ?? ''
  )
}

export const prefillWithUserLeadInfo = async (
  { analytics, geoLocationService }: { analytics: ApiService['analytics']; geoLocationService: GeoLocationService },
  email: string,
  content: TypeComponentFormFields['content'],
  setValue: (key: string, value: string | number) => void
) => {
  const userLeadInfo = await fetchUserLeadInfo(analytics, email)
  const prefilledFieldNames: string[] = []
  if (!userLeadInfo) {
    return prefilledFieldNames
  }

  const country = geoLocationService.countryName
  const userLeadInfoToUse = normalizeUserLeadInfo(userLeadInfo, country)

  for (const [key, value] of Object.entries(userLeadInfoToUse)) {
    let finalValue: string | number | undefined = ''
    const input = content.find((field) => field.fields.name === key)
    if (!input) {
      continue
    }

    switch (key as keyof ReturnType<typeof normalizeUserLeadInfo>) {
      case 'companySize': {
        const option = getCompanySizeOption(input as TypeComponentSelect, value as number)
        finalValue = getSelectOption(option).value
        break
      }
      case 'managementLevel': {
        const selectValue = ((value as string) ?? '').replaceAll('_', '-')
        const option = findMatchingSelectOption(input as TypeComponentSelect, selectValue)
        finalValue = getSelectOption(option).value
        break
      }
      case 'function': {
        const selectValue = remapTeamTypeValue(value as string)
        const option = findMatchingSelectOption(input as TypeComponentSelect, selectValue)
        finalValue = getSelectOption(option).value
        break
      }
      default:
        finalValue = value
        break
    }

    if (!finalValue) {
      continue
    }

    setValue(key, finalValue)
    prefilledFieldNames.push(key)
  }

  return prefilledFieldNames
}
